import { ChangeEvent, useState } from 'react';
import { getPresignedUrl, uploadToS3 } from '../../auth/_requests';
import { formatApiErrorMessage } from '../utils/error';
import { showToast } from '../utils/showToast';

const MAX_LEASE_FILE_SIZE_MB = 10;
const LEASE_FILE_TYPE = 'application/pdf';

export interface UploadedLease {
  url: string;
  downloadUrl: string;
  fileName: string;
}

interface UseLeaseUploadOptions {
  token: string | undefined;
  onUploaded: (lease: UploadedLease) => Promise<void> | void;
  onSelected?: (file: File) => void;
}

const validateLeaseFile = (file: File): string | null => {
  const isPdf = file.type === LEASE_FILE_TYPE || file.name.toLowerCase().endsWith('.pdf');
  if (!isPdf) {
    return 'Only PDF files are supported. Please choose a PDF of your lease.';
  }
  if (file.size > MAX_LEASE_FILE_SIZE_MB * 1024 * 1024) {
    return `This file is too large. Please upload a PDF smaller than ${MAX_LEASE_FILE_SIZE_MB} MB.`;
  }
  return null;
};

export const useLeaseUpload = ({ token, onUploaded, onSelected }: UseLeaseUploadOptions) => {
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fail = (message: string): void => {
    setUploadError(message);
    showToast({ message, type: 'error' });
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const input = event.target;
    const file = input.files?.[0];
    // Clearing the value lets the user pick the same file again after a failure
    input.value = '';
    if (!file || isUploading) return;

    setUploadError(null);
    const validationError = validateLeaseFile(file);
    if (validationError) {
      fail(validationError);
      return;
    }

    onSelected?.(file);
    setProgress(0);
    setIsUploading(true);
    try {
      const { url, uploadUrl, downloadUrl } = await getPresignedUrl(
        file.name,
        file.type,
        token,
        'lease-document'
      );
      await uploadToS3(file, uploadUrl, setProgress);
      await onUploaded({ url, downloadUrl, fileName: file.name });
    } catch (error) {
      console.error('Lease upload failed:', error);
      fail(
        formatApiErrorMessage(
          error instanceof Error ? error.message : '',
          'We could not upload your lease. Please check your connection and try again.'
        )
      );
    } finally {
      setIsUploading(false);
    }
  };

  return { isUploading, progress, uploadError, handleFileChange };
};
